'use client';
import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { FaBars, FaTimes } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { useUser } from '@/context/UserContext';
import ProblemReportOverlay, { FeedbackRefineContext, FeedbackSubmitResult } from './ProblemReportOverlay';
import FeedbackAreaPicker, { FeedbackSelection } from './FeedbackAreaPicker';
import { collectClientContext } from '@/app/utils/clientContext';
import { buildSupportDiagnosticSnapshot } from '@/app/lib/supportDiagnostics';
import { reportSubmissionToast, reportSupportReference, ReportSubmissionResponse } from '@/app/lib/supportToast';
import { getUserImage, logoUrl } from '../utils/utils';
import { ReactNode } from 'react';
import { useToast } from '@/context/ToastProvider';
import { ThemeDebug } from './ThemeDebug';
import { useHasSites } from '@/app/lib/useHasSites';
import { canAccessInstallerKnowledge } from '@/app/lib/installerKnowledgeAccess';


interface IINavBarProps {
    headerContent?: ReactNode;
}

// The open mobile menu has to fit between its own top edge and the bottom of
// the *visible* viewport. A fixed `100dvh - 4rem` cap is wrong whenever
// something sits above the navbar — the layout-level SubscriptionBanner, for
// instance — so measure the panel's actual offset instead, and re-measure on
// scroll/resize while it is open (the panel only mounts when open).
function MobileMenuScroll({ children }: { children: React.ReactNode }) {
    const ref = useRef<HTMLDivElement>(null);
    // SSR / pre-measure fallback: the common case of navbar-only clearance.
    const [maxHeight, setMaxHeight] = useState('calc(100dvh - 4rem)');

    useEffect(() => {
        const measure = () => {
            const el = ref.current;
            if (!el) return;
            const viewport = window.visualViewport;
            const height = viewport?.height ?? window.innerHeight;
            const offsetTop = viewport?.offsetTop ?? 0;
            const available = height + offsetTop - el.getBoundingClientRect().top;
            setMaxHeight(`${Math.max(0, available)}px`);
        };
        measure();
        window.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        window.visualViewport?.addEventListener('resize', measure);
        window.visualViewport?.addEventListener('scroll', measure);
        return () => {
            window.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
            window.visualViewport?.removeEventListener('resize', measure);
            window.visualViewport?.removeEventListener('scroll', measure);
        };
    }, []);

    return (
        <div
            ref={ref}
            data-testid="mobile-menu-scroll"
            style={{ maxHeight }}
            className="space-y-1 overflow-y-auto px-2 pb-3 pt-2"
        >
            {children}
        </div>
    );
}

const IINavBar = ({ headerContent }: IINavBarProps) => {
    const toast = useToast();
    const pathname = usePathname()
    const { user, installer, isAnonymous, isHacker, isInstaller, isNone, isActive } = useUser();
    // Rates only makes sense when there's a "site like yours" to compare
    // against — without one the numbers are misleading because system
    // design and install dominate the result. Staff (installer / hacker /
    // admin) always see Rates because they compare across portfolios.
    const { hasSites } = useHasSites();
    // While hasSites is still loading (undefined) keep the item visible so
    // the nav doesn't flicker — most signed-in users have a site. It only
    // drops out once we know for sure there's nothing to compare against.
    const showRates = isHacker || hasSites !== false;
    const showInstallerKnowledge = canAccessInstallerKnowledge(user);
    const showSiteAssistant = !isInstaller && !isHacker && !isNone && !isAnonymous && hasSites !== false;
    const installer_name = installer?.name || 'Powston';
    const [isProblemReportOpen, setIsProblemReportOpen] = useState(false);
    const [isSelectingFeedbackArea, setIsSelectingFeedbackArea] = useState(false);
    const [isRedoingFeedbackArea, setIsRedoingFeedbackArea] = useState(false);
    const [isPreparingFeedback, setIsPreparingFeedback] = useState(false);
    const [selectedFeedbackArea, setSelectedFeedbackArea] = useState<FeedbackSelection | null>(null);
    const [screenshot, setScreenshot] = useState('');
    const [screenshotSize, setScreenshotSize] = useState({ width: 0, height: 0 });
    const [showDebugUI, setShowDebugUI] = useState(false);

    const handleProblemReport = async (description: string, screenshot: string, url: string,
                                       selectedArea: string, cc_me: boolean,
                                       refine?: FeedbackRefineContext): Promise<FeedbackSubmitResult> => {
        try {
          // Capture browser/display/network state so support can rule out
          // client-side issues (dark mode rendering, narrow viewport, slow
          // connection, browser extension interference, etc.) without a
          // back-and-forth.
          const client_context = collectClientContext();
          const support_context = buildSupportDiagnosticSnapshot({
            actions: [],
            browser: client_context,
            url,
            screenshotAttached: !!screenshot,
            emailCopy: cc_me,
          });
          const response = await fetch('/api/report_problem', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              description,
              screenshot,
              url,
              cc_me,
              selectedArea,
              client_context,
              values: { support_context },
              // AI refine outcome from the feedback overlay, so the backend
              // can use the proposed fault domain when its own classifier
              // can't resolve one.
              fault_domain: refine?.fault_domain,
              ai_category: refine?.ai_category,
            }),
          });
    
          if (response.ok) {
            // The backend names the report's support owner ("Sent to REA
            // Global." on a partner-managed site) — surface that confirmation,
            // with any safety notice combined into the same toast (the
            // provider replaces toasts rather than stacking them).
            const data: ReportSubmissionResponse | null = await response.json().catch(() => null);
            const { type, message } = reportSubmissionToast(data);
            (type === 'info' ? toast.showInfo : toast.showSuccess)(message);
            return {
              reference: reportSupportReference(data) || undefined,
              message,
            };
          } else {
            toast.showError('Failed to report problem');
            throw new Error(`Problem report failed with status ${response.status}`);
          }
        } catch (error) {
          console.error('Error reporting problem:', error);
          if (!(error instanceof Error) || !error.message.startsWith('Problem report failed with status')) {
            toast.showError('An error occurred while reporting the problem');
          }
          throw error;
        }
      };

    const navigation = () => {
        if (isInstaller && user?.installer_id) {
                return [
                    { name: 'Home', href: '/', current: (pathname === '/') },
                    { name: 'Sites', href: '/sites', current: (pathname === '/sites') },
                    { name: 'Support queue', href: '/support', current: pathname.startsWith('/support') },
                    { name: 'Installer Admin', href: `/installer/${user.installer_id}`, current: (pathname === `/installer/${user.installer_id}`) },
                    { name: 'Leaderboard', href: '/rates', current: (pathname === '/rates'), secondary: true },
                    { name: 'Install / Recovery', href: '/configure', current: (pathname === '/configure'), secondary: true },
                    ...(showInstallerKnowledge ? [{ name: 'Knowledge', href: '/installer/knowledge', current: pathname === '/installer/knowledge', secondary: true }] : []),
                ];
            }
        if (isHacker) {
            return [
                { name: 'Home', href: '/', current: (pathname === '/') },
                { name: 'Sites', href: '/sites', current: (pathname === '/sites') },
                { name: 'Support queue', href: '/support', current: pathname.startsWith('/support') },
                { name: 'Leaderboard', href: '/rates', current: (pathname === '/rates'), secondary: true },
                { name: 'Install / Recovery', href: '/configure', current: (pathname === '/configure'), secondary: true },
                ...(showInstallerKnowledge ? [{ name: 'Knowledge', href: '/installer/knowledge', current: pathname === '/installer/knowledge', secondary: true }] : []),
            ]};
        if (isNone) {
            return [
                { name: 'Home', href: '/', current: (pathname === '/') },
            ]};
        if (isAnonymous) {
            return [
                { name: 'Login', href: '/login', current: (pathname === '/') },
            ]};
        // Signed-in owners: Settings gathers the per-site configuration
        // surfaces (site details, usage modes, tuning, notifications) that
        // otherwise hide behind small links, while Install / Recovery stays
        // the flow for connecting hardware.
        const onSettings = pathname === '/settings' || /^\/site\/[^/]+\/settings$/.test(pathname || '');
        return [
                { name: 'Home', href: '/', current: (pathname === '/') },
                ...(showSiteAssistant ? [{ name: 'Ask Powston', href: '/assistant', current: pathname.startsWith('/assistant') }] : []),
                { name: 'Settings', href: '/settings', current: onSettings },
                ...(showRates ? [{ name: 'Leaderboard', href: '/rates', current: (pathname === '/rates'), secondary: true }] : []),
                { name: 'Install / Recovery', href: '/configure', current: (pathname === '/configure'), secondary: true },
            ];
    };

    function classNames(...classes: string[]) {
        return classes.filter(Boolean).join(' ');
    };

    const openReportProblem = async (waitForUserMenu = false) => {
        if (isPreparingFeedback) return;
        setIsPreparingFeedback(true);
        setIsProblemReportOpen(false);
        setIsRedoingFeedbackArea(false);
        setSelectedFeedbackArea(null);

        // Give Headless UI one paint to close the invoking menu. The user menu
        // also has a 75 ms leave transition, so wait beyond it before
        // taking the screenshot or it will appear in the preview.
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
        if (waitForUserMenu) {
            await new Promise<void>((resolve) => window.setTimeout(resolve, 100));
        }
        try {
            const root = document.documentElement;
            setScreenshotSize({ width: root.scrollWidth, height: root.scrollHeight });
            const canvas = await html2canvas(document.body);
            setScreenshot(canvas.toDataURL());
        } catch (error) {
            // Area selection remains useful when browser screenshot capture is
            // blocked (for example by a cross-origin image on the page).
            console.warn('Unable to capture feedback screenshot:', error);
            setScreenshot('');
        } finally {
            setIsPreparingFeedback(false);
            setIsSelectingFeedbackArea(true);
        }
    };
    return (
        <>
        {/* Sticky so the menu (and the hamburger on phones) stays reachable on
            long pages like the site summary. z-40 sits above page content
            (max z-30) but below modals/assistant overlays (z-50). */}
        <Disclosure as="nav" className="sticky top-0 z-40 w-full bg-background border-b border-border">
            {({ open }) => (
                <>
                    <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
                        <div className="relative flex h-16 items-center justify-between">
                            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                                {/* Mobile menu button*/}
                                <DisclosureButton className="relative inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-muted hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary">
                                    <span className="absolute -inset-0.5" />
                                    <span className="sr-only">Open main menu</span>
                                    {open ? (
                                        <FaTimes className="block h-6 w-6" aria-hidden="true" />
                                    ) : (
                                        <FaBars className="block h-6 w-6" aria-hidden="true" />
                                    )}
                                </DisclosureButton>
                            </div>
                            <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                                <div className="hidden sm:flex flex-shrink items-center justify-start sm:justify-center">
                                    <div className="bg-background rounded p-1">
                                        <img
                                            className="h-12 w-auto"
                                            src={logoUrl(user?.installer_logo || 'powston-dots.svg')}
                                            alt={installer_name}
                                            onError={(e) => {
                                                // Installer logo failed to load — fall back to the
                                                // Powston mark rather than a broken-image icon.
                                                const img = e.currentTarget;
                                                const fallback = logoUrl('powston-dots.svg');
                                                if (!img.src.endsWith('powston-dots.svg')) img.src = fallback;
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="hidden sm:ml-6 sm:block">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                        {navigation().filter((item) => !item.secondary).map((item) => (
                                            <a
                                                key={'navigation=' + item.name}
                                                href={item.href}
                                                className={classNames(
                                                    item.current 
                                                        ? 'bg-primary text-primary-foreground' 
                                                        : 'text-foreground hover:bg-muted hover:text-accent-foreground',
                                                    'rounded-md px-2.5 lg:px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none'
                                                )}
                                                aria-current={item.current ? 'page' : undefined}
                                            >
                                                {item.name}
                                            </a>
                                        ))}
                                        {navigation().some((item) => item.secondary) && (
                                            <Menu as="div" className="relative">
                                                <MenuButton
                                                    className={classNames(
                                                        navigation().some((item) => item.secondary && item.current)
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'text-foreground hover:bg-muted hover:text-accent-foreground',
                                                        'rounded-md px-2.5 lg:px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none'
                                                    )}
                                                >
                                                    More
                                                </MenuButton>
                                                <MenuItems
                                                    anchor="bottom start"
                                                    className="z-50 mt-1 w-56 rounded-md border border-border bg-card p-1 shadow-lg focus:outline-none"
                                                >
                                                    {navigation().filter((item) => item.secondary).map((item) => (
                                                        <MenuItem key={`secondary-navigation-${item.name}`}>
                                                            {({ focus }) => (
                                                                <Link
                                                                    href={item.href}
                                                                    aria-current={item.current ? 'page' : undefined}
                                                                    className={classNames(
                                                                        focus ? 'bg-muted' : '',
                                                                        item.current ? 'font-semibold text-primary' : 'text-card-foreground',
                                                                        'block rounded px-3 py-2 text-sm transition-colors'
                                                                    )}
                                                                >
                                                                    {item.name}
                                                                </Link>
                                                            )}
                                                        </MenuItem>
                                                    ))}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                        <button
                                            type="button"
                                            data-testid="feedback-desktop"
                                            onClick={() => void openReportProblem()}
                                            disabled={isPreparingFeedback}
                                            className="text-foreground hover:bg-muted hover:text-accent-foreground rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-60"
                                        >
                                            {isPreparingFeedback ? 'Preparing…' : 'Send feedback'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute inset-y-0 right-0 flex items-center justify-end pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                                {!isAnonymous && <Menu as="div" className="relative ml-3">
                                    <div>
                                        <MenuButton aria-label="Open user menu" className="relative flex rounded-full bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background">
                                            <span className="absolute -inset-1.5" />
                                            <span className="sr-only">Open user menu</span>
                                            <img
                                                className="h-8 w-8 rounded-full"
                                                src={`${getUserImage(user)}`}
                                                alt={user?.name}
                                            />
                                        </MenuButton>
                                    </div>
                                    <Transition
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-card border border-border py-1 shadow-lg focus:outline-none">
                                                {showSiteAssistant ? <MenuItem>
                                                    {({ focus }) => (
                                                        <Link
                                                            href="/assistant"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '',
                                                                'block px-4 py-2 text-sm font-medium text-blue-700 transition-colors dark:text-blue-300'
                                                            )}
                                                        >
                                                            Ask Powston
                                                        </Link>
                                                    )}
                                                </MenuItem> : <></>}
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/profile"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Your Profile
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/settings"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Settings
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <button
                                                            type="button"
                                                            data-testid="feedback-user-menu"
                                                            onClick={() => void openReportProblem(true)}
                                                            disabled={isPreparingFeedback}
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '',
                                                                'block w-full px-4 py-2 text-left text-sm text-card-foreground transition-colors disabled:cursor-wait disabled:opacity-60'
                                                            )}
                                                        >
                                                            Help / Send feedback
                                                        </button>
                                                    )}
                                                </MenuItem>
                                                {(isInstaller && user?.installer_id) ? <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href={`/installer/${user.installer_id}`}
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Installer Admin
                                                        </a>
                                                    )}
                                                </MenuItem> : <></>}
                                                {showInstallerKnowledge ? <MenuItem>
                                                    {({ focus }) => (
                                                        <Link
                                                            href="/installer/knowledge"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '',
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Installer Knowledge
                                                        </Link>
                                                    )}
                                                </MenuItem> : <></>}
                                                {(isHacker || isActive) ? <MenuItem>
                                                    {({ focus }) => (
                                                        // Always route through the OAuth flow (not the static invite) so every
                                                        // click re-ensures the account link, server membership and @Verified
                                                        // role before landing in the server. Already-authorized users pass
                                                        // straight through (backend sends prompt=none).
                                                        <a
                                                            href="/api/discord/oauth/start"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '',
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Open Discord Server
                                                        </a>
                                                    )}
                                                </MenuItem> : <></>}
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/passkey_create"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Add Passkey
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="#"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setShowDebugUI(!showDebugUI);
                                                            }}
                                                        >
                                                            {showDebugUI ? 'Hide' : 'Show'} Theme Debug
                                                        </a>
                                                    )}
                                                </MenuItem>
                                                <MenuItem>
                                                    {({ focus }) => (
                                                        <a
                                                            href="/login"
                                                            className={classNames(
                                                                focus ? 'bg-muted' : '', 
                                                                'block px-4 py-2 text-sm text-card-foreground transition-colors'
                                                            )}
                                                        >
                                                            Sign in
                                                        </a>
                                                    )}
                                                </MenuItem>
                                            </MenuItems>
                                        </Transition>
                                </Menu>}
                                {headerContent && (
                                    <div className="ml-4 flex items-center max-w-1/2 w-1/2 overflow-hidden">{headerContent}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Cap the open menu to the space below the sticky bar so
                        every item stays reachable on short viewports. */}
                    <DisclosurePanel className="sm:hidden bg-background border-t border-border">
                        <MobileMenuScroll>
                            {navigation().map((item) => (
                                <DisclosureButton
                                    key={item.name}
                                    as="a"
                                    href={item.href}
                                    className={classNames(
                                        item.current 
                                            ? 'bg-primary text-primary-foreground' 
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                        'block rounded-md px-3 py-2 text-base font-medium transition-colors'
                                    )}
                                    aria-current={item.current ? 'page' : undefined}
                                >
                                    {item.name}
                                </DisclosureButton>
                            ))}
                            <DisclosureButton
                                as="button"
                                type="button"
                                data-testid="feedback-mobile"
                                onClick={() => void openReportProblem()}
                                disabled={isPreparingFeedback}
                                className="block w-full rounded-md px-3 py-2 text-left text-base font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-wait disabled:opacity-60"
                            >
                                Help / Send feedback
                            </DisclosureButton>
                        </MobileMenuScroll>
                    </DisclosurePanel>
                    <ThemeDebug enabled={showDebugUI} />
                </>
            )}
        </Disclosure>
        <FeedbackAreaPicker
          active={isSelectingFeedbackArea}
          onCancel={() => {
            setIsSelectingFeedbackArea(false);
            if (isRedoingFeedbackArea) {
              setIsRedoingFeedbackArea(false);
              setIsProblemReportOpen(true);
            }
          }}
          onSelect={(selection) => {
            setSelectedFeedbackArea(selection);
            setIsSelectingFeedbackArea(false);
            setIsRedoingFeedbackArea(false);
            setIsProblemReportOpen(true);
          }}
        />
        {selectedFeedbackArea && (
          <ProblemReportOverlay
            isOpen={isProblemReportOpen}
            preserveDraftWhenClosed={isRedoingFeedbackArea}
            onClose={() => {
              setIsProblemReportOpen(false);
              setIsRedoingFeedbackArea(false);
            }}
            onRedoArea={() => {
              setIsRedoingFeedbackArea(true);
              setIsProblemReportOpen(false);
              setIsSelectingFeedbackArea(true);
            }}
            onRemoveScreenshot={() => setScreenshot('')}
            screenshot={screenshot}
            screenshotSize={screenshotSize}
            selectedArea={selectedFeedbackArea}
            onSubmit={handleProblemReport}
          />
        )}
        </>
    )
}

export default IINavBar;
